import { gql, useQuery } from '@apollo/client';
import { Badge, DropdownMenu, IconButton, Text } from '@radix-ui/themes';
import { Loader, Pin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';

export const PINNED_JOBS_QUERY = gql`
  query pinnedJobs {
    pinnedJobs: viewer {
      id
      pinnedJobs {
        id
        title
        pinned
        location {
          name
          city
        }
        externalRef {
          externalId
          source
        }
      }
    }
  }
`;

type Job = { id: string; title: string; location: { name: string } };

const PinnedJobsList = ({ pinnedJobs }: { pinnedJobs: Job[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const gotoJob = (id: string) => {
    track(AnalyticsEvents.PINNED_JOB_OPENED);
    navigate('/job/' + id);
  };

  if (pinnedJobs.length === 0) {
    return (
      <DropdownMenu.Item disabled>
        <span>{t('nav.pinnedJobs.empty')}</span>
      </DropdownMenu.Item>
    );
  }

  return pinnedJobs.map((job, index) => (
    <DropdownMenu.Item
      key={index}
      onClick={() => {
        gotoJob(job.id);
      }}
      style={{ maxWidth: '160px' }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          width: '100%',
        }}
      >
        {job.title}
      </span>
      {job.location.name && (
        <Badge variant="soft" radius="large" color="indigo" className="ml-1">
          {job.location.name}
        </Badge>
      )}
    </DropdownMenu.Item>
  ));
};

const PinnedJobs = ({ asSubmenu = false }: { asSubmenu?: boolean }) => {
  const { t } = useTranslation();
  const [pinnedJobs, setPinnedJobs] = useState<Job[]>([]);

  const { data, loading, refetch } = useQuery(PINNED_JOBS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data && data.pinnedJobs?.pinnedJobs) {
      setPinnedJobs(sortJobs(data.pinnedJobs.pinnedJobs));
    }
  }, [data]);

  const sortJobs = (jobs: Job[]) => {
    const tempArray = [...jobs];

    tempArray.sort((a, b) => {
      if (a.title.toLowerCase() < b.title.toLowerCase()) {
        return -1;
      }
      if (a.title.toLowerCase() > b.title.toLowerCase()) {
        return 1;
      }

      return 0;
    });

    return tempArray;
  };

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      refetch();
    }
  };

  if (asSubmenu) {
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger className="dropdown-link flex items-center">
          <Pin size={18} className="mr-2" /> {t('nav.pinnedJobs.title')}
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent style={{ maxWidth: '180px' }}>
          <DropdownMenu.Label>
            <Text color="gray" highContrast weight="bold">
              {t('nav.pinnedJobs.title')}
            </Text>{' '}
            {loading && <Loader className="animate-spin ml-1" size={20} />}
          </DropdownMenu.Label>
          <DropdownMenu.Separator />
          <PinnedJobsList pinnedJobs={pinnedJobs} />
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    );
  }

  return (
    <DropdownMenu.Root onOpenChange={handleToggle}>
      <DropdownMenu.Trigger className="mx-2" style={{ backgroundColor: 'transparent' }}>
        <IconButton color="gray" highContrast aria-label={t('nav.pinnedJobs.title')} className="cursor-pointer">
          <Pin />
          <DropdownMenu.TriggerIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content color="gray" variant="soft" align="end">
        <DropdownMenu.Label>
          {t('nav.pinnedJobs.title')} {loading && <Loader className="animate-spin ml-1" size={20} />}
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        <PinnedJobsList pinnedJobs={pinnedJobs} />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default PinnedJobs;
